/**
 * Key hierarchy of the ACCOUNT (zero-knowledge root).
 *
 *   password ──Argon2id──▶ masterKey
 *      claveMaestra ──HKDF("master-wrap")──▶ wrapKey ──envuelve──▶ claveDeCuenta (aleatoria)
 *      masterKey ──HKDF("auth-verifier")─▶ authHash (to server, one-way)
 *   accountKey ──wraps──▶ ecdhPrivateKey  (public key travels in plaintext)
 *   accountKey ──derives──▶ personal vault keys / opens shared envelopes
 *
 * The accountKey is the root data key: it is generated randomly (does not derive from
 * the password) so that CHANGING THE PASSWORD only re-wraps the accountKey
 * (without forcing re-encryption of the whole vault). The password never encrypts data
 * directly; the masterKey only wraps the accountKey.
 */

import { base64ToBytes, bytesToBase64 } from "./encoding";
import { generateKeyPair } from "./asymmetric";
import {
  type Argon2Params,
  deriveAuthHash,
  deriveMasterKey,
  deriveMasterWrapKey,
  KDF_PRESETS,
  type KdfConfig,
  newKdfSaltBytes,
} from "./kdf";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { ProtectedAccount } from "./types";

const AAD_ACCOUNT_KEY = "boveda:account-key:v1";
const AAD_PRIVATE_KEY = "boveda:private-key:v1";

/** Unlocked state in memory (never serialized). */
export interface UnlockedAccount {
  readonly userId: string;
  /** Root data key (32 bytes). SECRET, in memory only. */
  readonly accountKey: Uint8Array;
  /** ECDH private key (PKCS#8 base64). SECRET, in memory only. */
  readonly privateKey: string;
  readonly publicKey: string;
}

export interface CreatedAccount {
  /** The only data sent to the server. */
  readonly protectedAccount: ProtectedAccount;
  /** Resulting unlocked state (remains on client). */
  readonly unlocked: UnlockedAccount;
}

/**
 * Creates a zero-knowledge account from the master password. Generates the
 * account key and ECDH pair, and produces the `ProtectedAccount` (blobs) for
 * the server. The server NEVER sees the password, the master key, or the account key
 * in plaintext.
 */
export async function createAccount(
  userId: string,
  password: string,
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<CreatedAccount> {
  const saltBytes = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(saltBytes), v: 1 };

  const masterKey = deriveMasterKey(password, saltBytes, params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  const authHash = await deriveAuthHash(masterKey);

  const accountKey = randomSymmetricKey();
  const keyPair = await generateKeyPair();

  const protectedAccountKey = await sealBase64(wrapKey, accountKey, AAD_ACCOUNT_KEY);
  const protectedPrivateKey = await sealBase64(
    accountKey,
    base64ToBytes(keyPair.privateKey),
    AAD_PRIVATE_KEY,
  );

  return {
    protectedAccount: {
      userId,
      kdf,
      protectedAccountKey,
      protectedPrivateKey,
      publicKey: keyPair.publicKey,
      authHash: bytesToBase64(authHash),
    },
    unlocked: {
      userId,
      accountKey,
      privateKey: keyPair.privateKey,
      publicKey: keyPair.publicKey,
    },
  };
}

/** Derives the authHash from the password (sent to the server during login). */
export async function computeAuthHash(password: string, kdf: KdfConfig): Promise<string> {
  const masterKey = deriveMasterKey(password, base64ToBytes(kdf.salt), kdf.params);
  return bytesToBase64(await deriveAuthHash(masterKey));
}

/**
 * Unlocks the account using the master password and the `ProtectedAccount`
 * retrieved from the server. If the password is incorrect, the AEAD open fails
 * (no need to ask the server: proof is cryptographic).
 */
export async function unlockAccount(
  password: string,
  account: ProtectedAccount,
): Promise<UnlockedAccount> {
  const masterKey = deriveMasterKey(password, base64ToBytes(account.kdf.salt), account.kdf.params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  let accountKey: Uint8Array;
  try {
    accountKey = await openBase64(wrapKey, account.protectedAccountKey, AAD_ACCOUNT_KEY);
  } catch {
    throw new Error("invalid master password");
  }
  const privateKeyBytes = await openBase64(
    accountKey,
    account.protectedPrivateKey,
    AAD_PRIVATE_KEY,
  );
  return {
    userId: account.userId,
    accountKey,
    privateKey: bytesToBase64(privateKeyBytes),
    publicKey: account.publicKey,
  };
}

/**
 * Changes the master password: re-wraps the SAME account key with the
 * new wrap-key (data is not re-encrypted). Returns updated fields
 * of the `ProtectedAccount`. Requires the account to be already unlocked.
 */
export async function changePassword(
  unlocked: UnlockedAccount,
  newPassword: string,
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<Pick<ProtectedAccount, "kdf" | "protectedAccountKey" | "authHash">> {
  const saltBytes = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(saltBytes), v: 1 };
  const masterKey = deriveMasterKey(newPassword, saltBytes, params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  const authHash = await deriveAuthHash(masterKey);
  const protectedAccountKey = await sealBase64(wrapKey, unlocked.accountKey, AAD_ACCOUNT_KEY);
  return { kdf, protectedAccountKey, authHash: bytesToBase64(authHash) };
}
